import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Radix radio group rendered as labelled rows. Options with a `hint`
 * get a second line in `paper-low`.
 */
export interface RadioOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly disabled?: boolean;
}

interface Props {
  readonly value: string | undefined;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly RadioOption[];
  readonly ariaLabel?: string;
  readonly orientation?: 'vertical' | 'horizontal';
  readonly className?: string;
  readonly testId?: string;
}

export function RadioGroup({
  value,
  onValueChange,
  options,
  ariaLabel,
  orientation = 'vertical',
  className,
  testId,
}: Props): JSX.Element {
  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cx('flex gap-2', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap gap-x-5', className)}
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={cx(
            'inline-flex cursor-pointer select-none items-start gap-2 text-body text-paper-mid',
            option.disabled === true && 'cursor-not-allowed opacity-60',
          )}
        >
          <RadioGroupPrimitive.Item
            value={option.value}
            disabled={option.disabled}
            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-line-strong bg-well transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 data-[state=checked]:border-signal disabled:pointer-events-none"
          >
            <RadioGroupPrimitive.Indicator className="h-2 w-2 rounded-full bg-signal" />
          </RadioGroupPrimitive.Item>
          <span className="flex flex-col gap-0.5">
            <span>{option.label}</span>
            {option.hint !== undefined && option.hint !== null ? (
              <span className="text-label text-paper-low">{option.hint}</span>
            ) : null}
          </span>
        </label>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
